import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ToggleButton from 'react-toggle-button';
import ReactJsAlert from 'reactjs-alert';
import { getToken } from '../../../../features/token/tokenSaver';
import { getScreenHostDetails } from '../../../../services/getScreenHostDetails';
import { updateScreenHost } from '../../../../services/updateScreenHost';
import { toggleScreenHostActive } from '../../../../services/toggleScreenHostActive';
import leftChevron from '../../../../assets/images/leftChevron.png';
import '../../AdminDashboardAdvertisers/AdvertiserDetails/AdvertiserDetails.css';

export default function ScreenHostDetails() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const token = useSelector(getToken);
    const [host, setHost] = useState(null);
    const [editData, setEditData] = useState({});
    const [error, setError] = useState(false);
    const [errorCode, setErrorCode] = useState('');
    const [success, setSuccess] = useState(false);
    const [successCode, setSuccessCode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        getScreenHostDetails(token, userId)
            .then((response) => {
                const h = response.data.host;
                setHost(h);
                setEditData({
                    fname: h.FName || '',
                    lname: h.LName || '',
                    email: h.Email || '',
                    phoneNumber: h.PhoneNumber || '',
                    city: h.City || '',
                    active: Boolean(h.Active),
                    businessName: h.BusinessName || '',
                    businessAddress: h.BusinessAddress || '',
                    businessCity: h.BusinessCity || '',
                    businessState: h.BusinessState || '',
                    businessZipCode: h.BusinessZipCode || '',
                    zellePhoneNumber: h.ZellePhoneNumber || '',
                    zelleEmail: h.ZelleEmail || '',
                    revenueSharePercent: String(h.RevenueSharePercent ?? 10),
                    password: ''
                });
            })
            .catch(() => {
                setError(true);
                setErrorCode('Could not load screen host.');
            });
    }, [token, userId]);

    function onEditChange(field, value) {
        setEditData((prev) => ({ ...prev, [field]: value }));
    }

    function onToggleActive(value) {
        const newActive = !value;
        toggleScreenHostActive(token, userId, newActive ? 1 : 0)
            .then(() => {
                onEditChange('active', newActive);
                setSuccess(true);
                setSuccessCode('Active status updated.');
            })
            .catch(() => {
                setError(true);
                setErrorCode('Failed to update active status.');
            });
    }

    function onSubmit(event) {
        event.preventDefault();
        setSubmitting(true);
        setError(false);

        const payload = {
            userId: parseInt(userId, 10),
            fname: editData.fname,
            lname: editData.lname,
            email: editData.email,
            phoneNumber: editData.phoneNumber,
            city: editData.city,
            active: editData.active ? 1 : 0,
            businessName: editData.businessName,
            businessAddress: editData.businessAddress,
            businessCity: editData.businessCity,
            businessState: editData.businessState,
            businessZipCode: editData.businessZipCode,
            zellePhoneNumber: editData.zellePhoneNumber || null,
            zelleEmail: editData.zelleEmail || null,
            revenueSharePercent: Number(editData.revenueSharePercent) || 10,
        };
        if (editData.password) {
            payload.password = editData.password;
        }

        updateScreenHost(payload, token)
            .then(() => {
                setSuccess(true);
                setSuccessCode('Screen host updated.');
                setSubmitting(false);
            })
            .catch((e) => {
                setError(true);
                setErrorCode(e.response?.data?.error || 'Update failed.');
                setSubmitting(false);
            });
    }

    if (!host) {
        return <p className="advertiserListTitle">Loading…</p>;
    }

    const agreementSigned = Boolean(host.AccountActivated);

    return (
        <div className="advertiserDetailsContainer">
            <ReactJsAlert
                status={error}
                type="error"
                title="Error"
                quotes={true}
                quote={errorCode}
                Close={() => setError(false)}
                color="#1E1E1E"
            />
            <ReactJsAlert
                status={success}
                type="success"
                title="Success"
                quotes={true}
                quote={successCode}
                Close={() => setSuccess(false)}
                color="#1E1E1E"
            />
            <img
                className="leftChevronScreenDetails"
                src={leftChevron}
                alt=""
                onClick={() => navigate('/adminDashboard/users/hosts')}
            />
            <span className="registeredScreensTitle advertiserListTitle">
                Screen Host #{host.UserId}
            </span>

            <div className="advertiserDetailsSummary">
                <p>
                    <strong>Agreement:</strong>{' '}
                    {agreementSigned
                        ? `Signed ${host.HostAgreementLegalName || ''} (${host.HostAgreementVersion || ''})`
                        : 'Pending first login'}
                </p>
                {host.HostAgreementAcceptedAt && (
                    <p>
                        <strong>Signed at:</strong> {new Date(host.HostAgreementAcceptedAt).toLocaleString()}
                    </p>
                )}
            </div>

            <form onSubmit={onSubmit} className="advertiserRegisterContainer advertiserEditForm">
                <div className="advertiserRegisterItemfull">
                    <span className="advertiserRegisterItemTitle">Active</span>
                    <ToggleButton value={editData.active || false} onToggle={onToggleActive} />
                </div>

                <div className="advertiserRegisterItemhalfright">
                    <span className="advertiserRegisterItemTitle">First Name</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.fname || ''}
                        onChange={(e) => onEditChange('fname', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfleft">
                    <span className="advertiserRegisterItemTitle">Last Name</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.lname || ''}
                        onChange={(e) => onEditChange('lname', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfright">
                    <span className="advertiserRegisterItemTitle">Email</span>
                    <input
                        className="advertiserRegisterItemInput"
                        type="email"
                        value={editData.email || ''}
                        onChange={(e) => onEditChange('email', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfleft">
                    <span className="advertiserRegisterItemTitle">Phone</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.phoneNumber || ''}
                        onChange={(e) => onEditChange('phoneNumber', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemfull">
                    <span className="advertiserRegisterItemTitle">New password (optional)</span>
                    <input
                        className="advertiserRegisterItemInput"
                        type="password"
                        placeholder="Leave blank to keep current"
                        value={editData.password || ''}
                        onChange={(e) => onEditChange('password', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemfull">
                    <span className="advertiserRegisterItemTitle">Business Name</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.businessName || ''}
                        onChange={(e) => onEditChange('businessName', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemfull">
                    <span className="advertiserRegisterItemTitle">Business Address</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.businessAddress || ''}
                        onChange={(e) => onEditChange('businessAddress', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfright">
                    <span className="advertiserRegisterItemTitle">Business City</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.businessCity || ''}
                        onChange={(e) => onEditChange('businessCity', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfleft">
                    <span className="advertiserRegisterItemTitle">Business State</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.businessState || ''}
                        onChange={(e) => onEditChange('businessState', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfright">
                    <span className="advertiserRegisterItemTitle">Business Zip</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.businessZipCode || ''}
                        onChange={(e) => onEditChange('businessZipCode', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemhalfleft">
                    <span className="advertiserRegisterItemTitle">Zelle Phone</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.zellePhoneNumber || ''}
                        onChange={(e) => onEditChange('zellePhoneNumber', e.target.value)}
                    />
                </div>
                <div className="advertiserRegisterItemfull">
                    <span className="advertiserRegisterItemTitle">Zelle Email</span>
                    <input
                        className="advertiserRegisterItemInput"
                        value={editData.zelleEmail || ''}
                        onChange={(e) => onEditChange('zelleEmail', e.target.value)}
                    />
                </div>

                <div className="advertiserRegisterItemhalfleft">
                    <span className="advertiserRegisterItemTitle">Revenue share %</span>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="advertiserRegisterItemInput"
                        value={editData.revenueSharePercent || '10'}
                        onChange={(e) => onEditChange('revenueSharePercent', e.target.value)}
                    />
                </div>

                <div className="advertiserEditFormBtnContainer">
                    <input
                        type="submit"
                        className="advertiserEditFormBtn"
                        value={submitting ? 'Saving…' : 'Save Changes'}
                        disabled={submitting}
                    />
                </div>
            </form>
        </div>
    );
}
